import React, { type PropsWithChildren, useCallback } from 'react'
import classnames from 'classnames'

import {
  setEditingFeed,
  useIsEditingAnyMix,
} from '../../../../firebase/hooks/useMixes'
import useFeatures from '../../../../sharedLegacy/hooks/useFeatures'
import {
  type MediaType,
  posToStyle,
  updateFeedPosition,
  useFeed,
} from '../../../layouts'

import './TrackPositioner.scss'

type Point = { x: number; y: number }

interface TrackPositionerProps {
  trackId: number
  trackType: MediaType
}

const diffPoints = (pointA: Point, pointB: Point) => {
  return {
    x: pointA.x - pointB.x,
    y: pointA.y - pointB.y,
  }
}

const attachMouse = (callback: (event: MouseEvent) => void) => {
  window.addEventListener('mousemove', callback)

  window.addEventListener('mouseup', () => {
    window.removeEventListener('mousemove', callback)
  })
}

const TrackPositioner = ({
  trackId,
  trackType,
  children,
}: PropsWithChildren<TrackPositionerProps>) => {
  const feed = useFeed(trackType, trackId)
  const isEditingAnyMix = useIsEditingAnyMix()
  const { HasWebinarMixesEnabled } = useFeatures()

  const attachAndMove = useCallback(
    (cEvent: React.MouseEvent<HTMLDivElement>) => {
      cEvent.nativeEvent.stopPropagation()
      // @ts-expect-error — TODO: better types
      setEditingFeed(feed.index)
      const startPoint = {
        x: cEvent.clientX,
        y: cEvent.clientY,
      }
      let startPosition: Point | undefined
      attachMouse((event) => {
        const { x, y } = diffPoints({ x: event.x, y: event.y }, startPoint)
        updateFeedPosition((f) => {
          if (!startPosition)
            startPosition = { x: f.crop.centerX, y: f.crop.centerY }

          const cY = f.crop.zoom > 30 ? y : -y

          const newPosition = {
            x: Math.min(1, Math.max(-1, startPosition.x + x / 150)),
            y: Math.min(1, Math.max(-1, startPosition.y + cY / 150)),
          }

          return {
            ...f,
            crop: {
              zoom: f.crop.zoom,
              centerX: newPosition.x,
              centerY: newPosition.y,
            },
          }
        })
      })
    },
    [feed?.index]
  )

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      className={classnames('track-positioner', `${trackType}-track`)}
      style={posToStyle(feed, { withAnchor: true })}
      onClick={
        HasWebinarMixesEnabled
          ? () => {
              if (isEditingAnyMix && feed) setEditingFeed(feed.index)
            }
          : undefined
      }
      onMouseDown={
        HasWebinarMixesEnabled && isEditingAnyMix ? attachAndMove : undefined
      }
    >
      <div className="track-wrapper">{children}</div>
    </div>
  )
}

export { TrackPositioner }
